import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { getBatchResults, deleteSubmission, getSubmissionDetail, saveBatchSnapshot, takeBatchSnapshot, getSnapshotStaleIds, saveBatchViewControls, takeBatchViewControls } from '../services/api';

const styles = {
  heading: { fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' },
  batchListSection: { marginBottom: '32px' },
  batchListTitle: { fontSize: '16px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' },
  batchTable: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  batchRow: { cursor: 'pointer', transition: 'background 0.1s' },
  batchBtn: { padding: '6px 16px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  th: { padding: '10px 14px', textAlign: 'left', background: '#f7fafc', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' },
  thSort: { cursor: 'pointer', userSelect: 'none' },
  td: { padding: '10px 14px', borderTop: '1px solid #e2e8f0', fontSize: '13px', color: '#4a5568' },
  ratingBadge: { padding: '3px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '700' },
  reviewBtn: { padding: '5px 10px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  pendingBadge: { padding: '3px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '700', background: '#fefcbf', color: '#975a16' },
  failedBadge: { padding: '3px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '700', background: '#fed7d7', color: '#c53030' },
  refreshBtn: { padding: '8px 16px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', marginLeft: '12px' },
  exportBtn: { padding: '8px 16px', background: '#38a169', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginLeft: '12px' },
  sectionTitle: { fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px', marginTop: '32px' },
  error: { background: '#fed7d7', color: '#c53030', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
  controlBar: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  input: { padding: '9px 12px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none', width: '220px' },
  select: { padding: '9px 12px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none' },
  count: { fontSize: '13px', color: '#718096', fontWeight: '600' },
};

// Rating badge colors aligned to the Monday board (#12079750290):
// E #9078C0, EE #FDFD96, ME #32de84, NI #FFB6C1. Text colors chosen for
// readable contrast on each background.
const ratingColors = {
  E: { bg: '#9078C0', color: '#ffffff' },
  EE: { bg: '#FDFD96', color: '#5c4d00' },
  ME: { bg: '#32de84', color: '#0f5132' },
  NI: { bg: '#FFB6C1', color: '#8a1c3b' },
  NS: { bg: '#e2e8f0', color: '#4a5568' },
};

function RatingBadge({ rating }) {
  if (!rating) return <span style={{ color: '#a0aec0' }}>-</span>;
  const colors = ratingColors[rating] || { bg: '#edf2f7', color: '#4a5568' };
  return (
    <span style={{ ...styles.ratingBadge, background: colors.bg, color: colors.color }}>
      {rating}
    </span>
  );
}

// Numeric rating from a percentage, matching the backend thresholds. Used only
// for the day-filtered view (the whole-batch rating comes from the backend).
function ratingFromScore(score) {
  if (score === null || score === undefined) return null;
  if (score >= 95) return 'E';
  if (score >= 86) return 'EE';
  if (score >= 75) return 'ME';
  return 'NI';
}

function formatAZ(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) + ' AZ';
  } catch { return isoString.split('T')[0]; }
}

function formatAZDate(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return isoString.split('T')[0]; }
}

export default function BatchResultsPage({ onViewVa, initialBatch }) {
  const [batchList, setBatchList] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingBatch, setLoadingBatch] = useState(null);

  // Trainee table controls
  const [traineeSearch, setTraineeSearch] = useState('');
  const [dayFilter, setDayFilter] = useState('');
  // TS-in-charge filter (Monday #12242062768). '' = all, '__unassigned' =
  // trainees with no TS on the Monday roster.
  const [tsFilter, setTsFilter] = useState('');
  const [sortKey, setSortKey] = useState('va_name');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    loadBatchList();
    if (initialBatch) {
      // Coming back from the VA Evaluation Overview: restore the snapshot
      // taken when this batch was last loaded (instant) instead of re-running
      // the whole-batch fetch (~15s on a large batch). Any submissions whose
      // scores changed since (overrides) are re-read fresh via millisecond
      // point-reads and patched in. No snapshot -> normal full fetch.
      const snap = takeBatchSnapshot(initialBatch);
      if (snap) { restoreSnapshot(snap); }
      else { handleSearchBatch(initialBatch); }
      // Restore the sticky trainee-table controls (TS filter, Day filter,
      // search, sort) saved before this page was unmounted for the VA
      // Evaluation Overview. These setters run after handleSearchBatch's
      // synchronous control resets in the same tick, so the saved values
      // win on either restore path.
      const controls = takeBatchViewControls(initialBatch);
      if (controls) {
        setTraineeSearch(controls.traineeSearch || '');
        setDayFilter(controls.dayFilter || '');
        setTsFilter(controls.tsFilter || '');
        setSortKey(controls.sortKey || 'va_name');
        setSortDir(controls.sortDir || 'asc');
      }
    }
  }, []);

  // Keep the saved controls current so "Back to All Trainees" returns to the
  // exact table view the trainer left (Monday #12242062768 follow-up: the TS
  // dropdown reset to All TS after viewing a trainee's details).
  useEffect(() => {
    if (data && data.batch) {
      saveBatchViewControls(data.batch, { traineeSearch, dayFilter, tsFilter, sortKey, sortDir });
    }
  }, [data, traineeSearch, dayFilter, tsFilter, sortKey, sortDir]);

  async function loadBatchList() {
    try {
      const result = await getBatchResults('');
      setBatchList(result.batch_list || []);
    } catch { /* ignore */ }
  }

  async function handleDelete(submissionId, vaName) {
    if (!window.confirm(`Delete submission from ${vaName}? This cannot be undone.`)) return;
    setError('');
    try {
      const result = await deleteSubmission(submissionId);
      if (result.error) { setError(result.error); }
      else { handleSearchBatch(data?.batch); loadBatchList(); }
    } catch { setError('Failed to delete submission.'); }
  }

  async function handleSearchBatch(batch) {
    if (!batch) return;
    setError('');
    setLoading(true);
    setLoadingBatch(batch);
    // Reset trainee-table controls when loading a different batch
    setTraineeSearch('');
    setDayFilter('');
    try {
      const result = await getBatchResults(batch);
      if (result.error && !result.submissions) {
        setError(result.error);
        setData(null);
      } else {
        setData(result);
        saveBatchSnapshot(batch, result);
      }
    } catch { setError('Failed to load results.'); }
    finally { setLoading(false); setLoadingBatch(null); }
  }

  // Restore the navigation snapshot, point-reading any submissions that were
  // overridden since it was taken and folding the fresh scores into both the
  // submission rows and the affected VAs' summary averages. Falls back to the
  // full fetch if any point-read fails, so a restored view is never wrong.
  async function restoreSnapshot(snap) {
    const staleIds = getSnapshotStaleIds();
    if (!staleIds.length) { setData(snap); return; }
    try {
      const details = await Promise.all(staleIds.map((id) => getSubmissionDetail(id)));
      const byId = {};
      staleIds.forEach((id, i) => {
        const d = details[i];
        if (!d || d.error) throw new Error('point-read failed');
        byId[id] = d;
      });
      const submissions = (snap.submissions || []).map((s) => {
        const d = byId[s.submission_id];
        if (!d) return s;
        return {
          ...s,
          grand_total: d.grand_total !== undefined ? d.grand_total : s.grand_total,
          rating: d.rating !== undefined ? d.rating : s.rating,
          grading_status: d.grading_status || s.grading_status,
          override_flag: d.override_flag || s.override_flag,
          excluded_from_average: d.excluded_from_average !== undefined ? d.excluded_from_average : s.excluded_from_average,
        };
      });
      // Key on trainee_key, not va_name (Monday #12017317679): a trainee
      // renamed mid-batch has rows under two spellings, so a name-keyed patch
      // recomputed only half their scores into the average. `tkey` falls back
      // to the name so a snapshot captured before this field existed can never
      // collapse every trainee onto a single undefined key.
      const tkey = (x) => x.trainee_key || `nm:${(x.va_name || '').toLowerCase()}`;
      const affectedVas = new Set(submissions.filter((s) => byId[s.submission_id]).map(tkey));
      const summary = (snap.summary || []).map((sm) => {
        if (!affectedVas.has(tkey(sm))) return sm;
        const scores = submissions
          .filter((s) => tkey(s) === tkey(sm) && s.grading_status === 'complete' && !s.excluded_from_average)
          .map((s) => parseFloat(s.grand_total))
          .filter((n) => !isNaN(n));
        // No graded scores -> null average / null rating (renders '-'),
        // matching the backend's absent-average semantics.
        const avg = scores.length ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) / 100 : null;
        return { ...sm, average_score: avg, rating: sm.rating === 'NS' ? 'NS' : ratingFromScore(avg) };
      });
      const patched = { ...snap, submissions, summary };
      setData(patched);
      saveBatchSnapshot(snap.batch, patched);
    } catch {
      handleSearchBatch(snap.batch);
    }
  }

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function sortArrow(key) {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  function viewVa(traineeKey) {
    // Always open the full (whole-batch) detail, regardless of any day filter.
    // Selection is by the stable trainee key so a renamed trainee opens with
    // ALL their submissions (Monday #12017317679).
    const s = (data.summary || []).find((x) => x.trainee_key === traineeKey) || {};
    const vaSubs = (data.submissions || []).filter((sub) => sub.trainee_key === traineeKey);
    onViewVa(
      s.va_name || '',
      data.batch,
      vaSubs,
      s.grading_complete,
      s.missing_graded || [],
      s.ts_name || '',
      { cId: s.c_id || '', vaEmail: s.va_email || '', traineeKey },
    );
  }

  function handleExportExcel() {
    if (!data || !data.submissions) return;

    const subs = [...data.submissions]
      .filter(s => s.grading_status === 'complete' || s.grading_status === 'archived')
      .sort((a, b) => {
        const nameCompare = (a.va_name || '').toLowerCase().localeCompare((b.va_name || '').toLowerCase());
        if (nameCompare !== 0) return nameCompare;
        return (a.submission_date || '').localeCompare(b.submission_date || '');
      });

    const vaSummary = {};
    for (const s of (data.summary || [])) {
      vaSummary[s.trainee_key] = { avg: s.average_score, rating: s.rating, ts: s.ts_name || '' };
    }

    const header = [
      'VA Name', 'VA Email', 'TS', 'Average Score', 'Overall Rating',
      'Day', 'Case', 'Date Submitted', 'Time Submitted',
      'Status', 'Score', 'Rating',
      'HPI', 'ROS', 'Objective', 'Assessment', 'Plan',
      'Organization', 'Error', 'Grammar', 'Client Preference', 'AHDI Rules',
    ];
    const rows = [header];

    for (const sub of subs) {
      let dateStr = '';
      let timeStr = '';
      if (sub.submission_date) {
        try {
          const d = new Date(sub.submission_date);
          dateStr = d.toLocaleDateString('en-US', { timeZone: 'America/Phoenix', month: '2-digit', day: '2-digit', year: 'numeric' });
          timeStr = d.toLocaleTimeString('en-US', { timeZone: 'America/Phoenix', hour: 'numeric', minute: '2-digit', hour12: true }) + ' AZ';
        } catch { dateStr = sub.submission_date; }
      }
      const statusLabel = (sub.no_submission ? 'not_submitted' : (sub.grading_status || ''))
        + (sub.excluded_from_average ? ' (excluded from avg)' : '');
      const va = vaSummary[sub.trainee_key] || {};
      rows.push([
        sub.va_name || '',
        sub.va_email || '',
        va.ts || '',
        (va.avg !== undefined && va.avg !== null) ? (va.avg + '%') : '--',
        va.rating || '',
        sub.day_name || '',
        sub.scenario || '',
        dateStr,
        timeStr,
        statusLabel,
        sub.grading_status === 'complete' ? (sub.grand_total + '%') : '',
        sub.grading_status === 'complete' ? (sub.rating || '') : '',
        sub.hpi_score || '',
        sub.ros_score || '',
        sub.objective_score || '',
        sub.assessment_score || '',
        sub.plan_score || '',
        sub.organization || '',
        sub.error_category || sub.error || '',
        sub.grammar || '',
        sub.client_preference || '',
        sub.ahdi_rules || '',
      ]);
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [
      { wch: 25 }, { wch: 30 }, { wch: 22 }, { wch: 14 }, { wch: 12 },
      { wch: 8 }, { wch: 25 }, { wch: 14 }, { wch: 14 },
      { wch: 10 }, { wch: 8 }, { wch: 8 },
      { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 8 },
      { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 18 }, { wch: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Batch ${data.batch}`);
    XLSX.writeFile(wb, `Batch_${data.batch}_Results.xlsx`);
  }

  // ---- Derive the trainee rows ----
  // Heavy step: group submissions by VA once and build per-VA rows. Memoized
  // on [data, dayFilter] so it does NOT rerun on every search keystroke or
  // sort click. A batch can have ~22k submissions, and the old approach both
  // ran on every render and filtered the full list per VA (O(VAs x subs)) -
  // the main source of UI lag. Grouping once is O(subs).
  const { baseRows, days, tsOptions } = useMemo(() => {
    if (!data) return { baseRows: [], days: [], tsOptions: [] };
    const subs = data.submissions || [];
    // TS names come from the backend per-VA summary (live Monday roster).
    const tsByVa = {};
    for (const s of (data.summary || [])) tsByVa[s.trainee_key] = s.ts_name || '';
    const tsOptions = Array.from(new Set(Object.values(tsByVa).filter(Boolean))).sort();
    const days = Array.from(new Set(subs.map((s) => s.day_name).filter(Boolean)))
      .sort((a, b) => {
        const na = parseInt((a.match(/\d+/) || ['999'])[0], 10);
        const nb = parseInt((b.match(/\d+/) || ['999'])[0], 10);
        return na - nb;
      });

    // Group on trainee_key, not va_name (Monday #12017317679). Name grouping
    // forked a trainee into two table rows the moment a TS edited their
    // roster entry, each with a partial average.
    const subsByVa = {};
    for (const sub of subs) {
      (subsByVa[sub.trainee_key] = subsByVa[sub.trainee_key] || []).push(sub);
    }

    let baseRows;
    if (!dayFilter) {
      // Whole-batch view: use the backend per-VA summary.
      baseRows = (data.summary || []).map((s) => {
        const vaSubs = subsByVa[s.trainee_key] || [];
        return {
          va_name: s.va_name,
          trainee_key: s.trainee_key,
          c_id: s.c_id || '',
          va_email: s.va_email || '',
          ts_name: s.ts_name || '',
          submissions_count: s.submissions_count,
          average_score: s.average_score,
          rating: s.rating,
          grading_complete: s.grading_complete,
          latestDate: vaSubs.map((x) => x.submission_date || '').filter(Boolean).sort().pop() || '',
          hasPending: vaSubs.some((x) => x.grading_status !== 'complete' && x.grading_status !== 'failed_permanent' && x.grading_status !== 'archived'),
          hasFailed: vaSubs.some((x) => x.grading_status === 'failed_permanent'),
        };
      });
    } else {
      // Day-scoped view: recompute each VA's row from that day's submissions.
      baseRows = [];
      for (const [traineeKey, allVaSubs] of Object.entries(subsByVa)) {
        const vaSubs = allVaSubs.filter((x) => x.day_name === dayFilter);
        if (!vaSubs.length) continue;
        const summaryRow = (data.summary || []).find((x) => x.trainee_key === traineeKey) || {};
        const scores = vaSubs
          .filter((x) => x.grading_status === 'complete' && !x.excluded_from_average)
          .map((x) => parseFloat(x.grand_total))
          .filter((n) => !isNaN(n));
        const avg = scores.length ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) / 100 : null;
        baseRows.push({
          va_name: summaryRow.va_name || vaSubs[vaSubs.length - 1].va_name || '',
          trainee_key: traineeKey,
          c_id: summaryRow.c_id || '',
          va_email: summaryRow.va_email || '',
          ts_name: tsByVa[traineeKey] || '',
          submissions_count: vaSubs.length,
          average_score: avg,
          rating: ratingFromScore(avg),
          grading_complete: false,
          latestDate: vaSubs.map((x) => x.submission_date || '').filter(Boolean).sort().pop() || '',
          hasPending: vaSubs.some((x) => x.grading_status !== 'complete' && x.grading_status !== 'failed_permanent' && x.grading_status !== 'archived'),
          hasFailed: vaSubs.some((x) => x.grading_status === 'failed_permanent'),
        });
      }
    }
    return { baseRows, days, tsOptions };
  }, [data, dayFilter]);

  // Cheap step: filter by search + sort over the ~hundreds of per-VA rows.
  const displayedRows = useMemo(() => {
    const term = traineeSearch.trim().toLowerCase();
    let rows = term ? baseRows.filter((r) => (r.va_name || '').toLowerCase().includes(term)) : baseRows;
    if (tsFilter === '__unassigned') {
      rows = rows.filter((r) => !r.ts_name);
    } else if (tsFilter) {
      rows = rows.filter((r) => r.ts_name === tsFilter);
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (sortKey) {
        case 'ts': return ((a.ts_name || '').toLowerCase().localeCompare((b.ts_name || '').toLowerCase())) * dir;
        case 'submissions': return ((a.submissions_count || 0) - (b.submissions_count || 0)) * dir;
        case 'latest': return ((a.latestDate || '').localeCompare(b.latestDate || '')) * dir;
        case 'average': {
          const av = (a.average_score === null || a.average_score === undefined) ? -1 : a.average_score;
          const bv = (b.average_score === null || b.average_score === undefined) ? -1 : b.average_score;
          return (av - bv) * dir;
        }
        default: return (a.va_name || '').toLowerCase().localeCompare((b.va_name || '').toLowerCase()) * dir;
      }
    });
  }, [baseRows, traineeSearch, tsFilter, sortKey, sortDir]);

  return (
    <div>
      <h1 style={styles.heading}>Batch Results</h1>
      {error && <div style={styles.error}>{error}</div>}

      {/* Loading a specific batch: show progress instead of flashing the
          batch quick-select list (which made back-navigation look like it
          detoured through "all batches"). */}
      {!data && loadingBatch && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#718096', fontSize: '14px' }}>
          Loading trainees for Batch {loadingBatch}...
        </div>
      )}

      {/* Batch Quick-Select List */}
      {batchList.length > 0 && !data && !loadingBatch && (
        <div style={styles.batchListSection}>
          <div style={styles.batchListTitle}>Batches with Submissions</div>
          <table style={styles.batchTable}>
            <thead>
              <tr>
                <th style={styles.th}>Batch</th>
                <th style={styles.th}>Submissions</th>
                <th style={styles.th}>Archived</th>
                <th style={styles.th}>Graded</th>
                <th style={styles.th}>Failures</th>
                <th style={styles.th}>Last Graded</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {batchList.map((b) => (
                <tr key={b.batch} style={styles.batchRow}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = ''}
                >
                  <td style={{ ...styles.td, fontWeight: '600', fontSize: '15px' }}>Batch {b.batch}</td>
                  <td style={styles.td}>{b.submissions} submission{b.submissions !== 1 ? 's' : ''}</td>
                  <td style={styles.td}>{b.archived || 0}</td>
                  <td style={styles.td}>{b.graded || 0}</td>
                  <td style={{
                    ...styles.td,
                    color: (b.failed || 0) > 0 ? '#c53030' : '#2d3748',
                    fontWeight: (b.failed || 0) > 0 ? '600' : '400',
                  }}>
                    {b.failed || 0}
                  </td>
                  <td style={styles.td}>{formatAZDate(b.latest_graded_at)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button style={styles.batchBtn} onClick={() => handleSearchBatch(b.batch)}>
                      View Results
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <>
          <button
            style={{ background: 'none', border: 'none', color: '#9C3D97', cursor: 'pointer', fontSize: '14px', marginBottom: '16px', padding: 0 }}
            onClick={() => { setData(null); loadBatchList(); }}
          >
            &larr; Back to all batches
          </button>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <h2 style={styles.sectionTitle}>All Trainees - Batch {data.batch}</h2>
            <button style={styles.exportBtn} onClick={handleExportExcel}>
              Batch Export Results
            </button>
            <button style={styles.refreshBtn} onClick={() => handleSearchBatch(data.batch)} disabled={loading}>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {/* Trainee table controls: search, day filter, count */}
          <div style={styles.controlBar}>
            <input
              style={styles.input}
              placeholder="Search trainee by name..."
              value={traineeSearch}
              onChange={(e) => setTraineeSearch(e.target.value)}
            />
            {days.length > 0 && (
              <select style={styles.select} value={dayFilter} onChange={(e) => setDayFilter(e.target.value)}>
                <option value="">All days</option>
                {days.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
            <select style={styles.select} value={tsFilter} onChange={(e) => setTsFilter(e.target.value)} title="Filter trainees by Training Specialist (assigned on the Monday roster board)">
              <option value="">All TS</option>
              {tsOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              <option value="__unassigned">Unassigned</option>
            </select>
            <span style={styles.count}>
              {displayedRows.length} trainee{displayedRows.length !== 1 ? 's' : ''}
              {dayFilter ? ` (${dayFilter})` : ''}
              {tsFilter ? ` (TS: ${tsFilter === '__unassigned' ? 'Unassigned' : tsFilter})` : ''}
            </span>
          </div>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.thSort }} onClick={() => toggleSort('va_name')}>VA Name{sortArrow('va_name')}</th>
                <th style={{ ...styles.th, ...styles.thSort }} onClick={() => toggleSort('ts')}>TS{sortArrow('ts')}</th>
                <th style={{ ...styles.th, ...styles.thSort }} onClick={() => toggleSort('submissions')}>Submissions{sortArrow('submissions')}</th>
                <th style={{ ...styles.th, ...styles.thSort }} onClick={() => toggleSort('latest')}>Latest Date{sortArrow('latest')}</th>
                <th style={{ ...styles.th, ...styles.thSort }} onClick={() => toggleSort('average')}>Average Score{sortArrow('average')}</th>
                <th style={styles.th}>Rating</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.map((r, i) => (
                <tr key={i}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = ''}
                >
                  <td style={{ ...styles.td, fontWeight: '600' }}>{r.va_name}</td>
                  <td style={styles.td}>{r.ts_name || '-'}</td>
                  <td style={styles.td}>
                    {r.submissions_count}{!dayFilter ? ' / 75' : ''}
                    {r.hasPending && <span style={{ ...styles.pendingBadge, marginLeft: '8px' }}>Grading...</span>}
                    {r.hasFailed && <span style={{ ...styles.failedBadge, borderRadius: '4px', fontSize: '11px', marginLeft: '8px' }}>GRADING FAILED</span>}
                    {r.grading_complete && <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', background: '#c6f6d5', color: '#276749', marginLeft: '8px' }}>GRADING COMPLETE</span>}
                  </td>
                  <td style={styles.td}>{formatAZDate(r.latestDate)}</td>
                  <td style={styles.td}><strong>{(r.average_score === null || r.average_score === undefined) ? '-' : r.average_score + '%'}</strong></td>
                  <td style={styles.td}><RatingBadge rating={r.rating} /></td>
                  <td style={styles.td}>
                    <button style={styles.reviewBtn} onClick={() => viewVa(r.trainee_key)}>
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
              {displayedRows.length === 0 && (
                <tr><td style={{ ...styles.td, textAlign: 'center', color: '#a0aec0' }} colSpan={7}>
                  {traineeSearch || dayFilter || tsFilter ? 'No trainees match the current filter.' : 'No submissions found for this batch.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
